import { config } from '@uhg-abyss/web/tools/config';
import { storage } from '@uhg-abyss/web/tools/storage';
import { useEffect, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { Constants } from '../../common/Constants';
import { getCategoryFromChipCategoriesCount } from '../../common/RecentActivity/helper';
import { getCurrentUTCYear } from '../../common/Utils/datesUtils/formatDate';
import { useChipStore } from '../../store/useChipStore';
import { ChipState } from '../../store/useChipStore/chipStore';
import { usePCPStore } from '../../store/usePCPStore';
import { PCPSearchStore } from '../../store/usePCPStore/pcpStore';
import { getPlanYear } from '../../utils/providerSearch.utils';
import { useCurrentMemberOrDependentInfo } from '../useCurrentMember';
import { useCustomQuery } from '../useCustomQuery';
import { useHsidUuid } from '../useHsidUuid';
import DeleteRecentSearch from './DeleteRecentSearch.graphql';
import GetRecentSearches from './RecentSearches.graphql';
import SaveRecentSearch from './SaveRecentSearch.graphql';

export interface RecentSearchesParams {
  psxSearchTerm: string;
  psxSearchTerm_en?: string;
  pesSearchTerm: string;
  searchType: string;
  category: string;
  location: string;
  latitude: string;
  longitude: string;
  coverageType: string;
  isMixedSearchSuggestion?: boolean;
  isUserTextSearch?: boolean;
  planYear: boolean;
  virtualCare?: boolean;
  gender?: string;
  conditions?: string;
  specialtyCategories?: string;
  bhProgramFacilityAgg?: string;
  aoeCodes?: string[];
  behavioralProgramId?: string[];
  skipSave?: boolean;
}

export interface DeleteRecentSearchVariables {
  clearAll: boolean;
  psxSearchTerm?: string;
  pesSearchTerm?: string;
  createdDate?: string;
  planYear?: number;
  userId?: string;
}

export const useDependentSeqNbr = () =>
  usePCPStore(
    useShallow((state: PCPSearchStore) => ({
      dependentSeqNbr: state.pcpSearchState.dependentSeqNbr,
    }))
  ).dependentSeqNbr;

export const usePlanYear = () => {
  const { coverageType } = useChipStore(
    useShallow((state: ChipState) => ({
      coverageType: state.coverageType,
    }))
  );
  const dependentSeqNbr = useDependentSeqNbr();
  const currentMember = useCurrentMemberOrDependentInfo(dependentSeqNbr);
  const memberPlanYear = getPlanYear(currentMember, coverageType);
  const currentYear = getCurrentUTCYear();

  if (currentMember?.isPreEffective) {
    return [memberPlanYear];
  } else if (currentYear > 2024) {
    return [currentYear];
  } else {
    return [null];
  }
};

export const useGetRecentSearches = (options) => {
  const [recentSearchesData, setRecentSearchesData] = useState({});
  const [queryResult, queryAction] = useCustomQuery(GetRecentSearches, {
    ...options,
    url: config('GRAPHQL_API_URL'),
    requestPolicy: 'no-cache',
    accessor: 'recentSearches',
    onCompleted: (result) => {
      if (options?.onCompleted) {
        options.onCompleted(result?.data);
      }
      setRecentSearchesData(result?.data);
    },
  });

  const parsedResult = {
    ...queryResult,
    data: recentSearchesData,
  };

  return [parsedResult, queryAction];
};

export const useSaveRecentSearches = (options) => {
  const [recentSearchesData, setRecentSearchesData] = useState({});
  const [queryResult, queryAction] = useCustomQuery(SaveRecentSearch, {
    ...options,
    url: config('GRAPHQL_API_URL'),
    accessor: 'saveRecentSearch',
    requestPolicy: 'no-cache',
    onCompleted: (result) => {
      if (options?.onCompleted) {
        options.onCompleted(result?.data);
      }
      setRecentSearchesData(result?.data);
    },
  });

  const parsedResult = {
    ...queryResult,
    data: recentSearchesData,
  };

  return [parsedResult, queryAction];
};

export const useGetRecentSearchesResults = () => {
  const [recentSearches, getRecentSearches] = useGetRecentSearches({});
  const hsidUuid = useHsidUuid();

  const isSuperUser =
    storage.session.get(Constants.STORAGE_KEYS.SESSION.SUPER_USER) || false;

  const isGuestUser =
    storage.session.get(Constants.STORAGE_KEYS.SESSION.IS_GUEST) || false;

  const [planYear] = usePlanYear();

  useEffect(() => {
    if (!isSuperUser && !isGuestUser) {
      getRecentSearches({
        variables: { planYear, ...(hsidUuid && { userId: hsidUuid }) },
      });
    }
  }, [isSuperUser, isGuestUser, planYear]);

  return recentSearches;
};

export const useSaveRecentSearchesResults = ({
  ...variables
}: RecentSearchesParams) => {
  const [saveRecentSearches, getSaveRecentSearches] = useSaveRecentSearches({});
  const [planYear] = usePlanYear();
  const isSuperUser =
    storage.session.get(Constants.STORAGE_KEYS.SESSION.SUPER_USER) || false;
  const { pesSearchTerm, category, skipSave } = variables;
  const hsidUuid = useHsidUuid();

  const dependentSeqNbr = useDependentSeqNbr();
  const currentMember = useCurrentMemberOrDependentInfo(dependentSeqNbr);
  const isGuestUser =
    storage.session.get(Constants.STORAGE_KEYS.SESSION.IS_GUEST) ||
    currentMember?.isIFPEarlyPay ||
    false;

  useEffect(() => {
    if (
      pesSearchTerm &&
      category &&
      !isSuperUser &&
      !isGuestUser &&
      !skipSave
    ) {
      getSaveRecentSearches({
        variables: {
          ...variables,
          category: getCategoryFromChipCategoriesCount(category),
          coverageType: variables.coverageType || 'M',
          isMixedSearchSuggestion: variables.isMixedSearchSuggestion || false,
          isUserTextSearch: variables.isUserTextSearch || false,
          planYear,
          ...(hsidUuid && { userId: hsidUuid }),
        },
      });
    }
  }, [pesSearchTerm, isSuperUser, isGuestUser, category, planYear, skipSave]);

  return saveRecentSearches;
};

export const useDeleteRecentSearches = (options) => {
  const [deleteRecentSearchData, setDeleteRecentSearchData] = useState({});
  const [queryResult, queryAction] = useCustomQuery(DeleteRecentSearch, {
    ...options,
    url: config('GRAPHQL_API_URL'),
    accessor: 'deleteRecentSearch',
    requestPolicy: 'no-cache',
    onCompleted: (result) => {
      if (options?.onCompleted) {
        options.onCompleted(result?.data);
      }
      setDeleteRecentSearchData(result?.data);
    },
  });

  const parsedResult = {
    ...queryResult,
    data: deleteRecentSearchData,
  };

  return [parsedResult, queryAction];
};
